import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Utility functions for Portfolio component animations
 */

// Initialize hero section animations
export const initHeroAnimations = () => {
  // Animate hero elements
  gsap.fromTo(
    '.portfolio-hero-title',
    { opacity: 0, y: 50 },
    { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }
  );
  
  gsap.fromTo(
    '.portfolio-hero-subtitle',
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0, duration: 1, delay: 0.3, ease: 'power3.out' }
  );
  
  // Animate SVG elements
  const svgs = document.querySelectorAll('.animated-svg');
  svgs.forEach((svg, index) => {
    gsap.fromTo(
      svg,
      { opacity: 0, scale: 0.8 },
      { 
        opacity: 0.15, 
        scale: 1, 
        duration: 1.5, 
        delay: 0.5 + (index * 0.2), 
        ease: 'elastic.out(1, 0.5)' 
      }
    );
  });
  
  // Animate particles
  const particles = document.querySelectorAll('.portfolio-floating-particle');
  particles.forEach((particle, index) => {
    gsap.fromTo(
      particle,
      { opacity: 0 },
      { opacity: 1, duration: 1, delay: 1 + (index * 0.1), ease: 'power2.out' }
    );
  });
};

// Portfolio filter animation
export const animateFilterChange = (element, isActive) => {
  if (isActive) {
    gsap.to(element, {
      backgroundColor: 'rgba(75, 108, 183, 1)',
      color: 'white',
      scale: 1.05,
      duration: 0.3,
      ease: 'power2.out'
    });
  } else {
    gsap.to(element, {
      backgroundColor: 'white',
      color: '#333',
      scale: 1,
      duration: 0.3,
      ease: 'power2.out'
    });
  }
};

// Animate portfolio items when they appear
export const animatePortfolioItems = (items) => {
  gsap.fromTo(
    items,
    { 
      opacity: 0,
      y: 30
    },
    { 
      opacity: 1,
      y: 0,
      duration: 0.5,
      stagger: 0.1,
      ease: 'power2.out'
    }
  );
};

// Create staggered entrance for portfolio items
export const staggerPortfolioItems = (selector) => {
  const items = document.querySelectorAll(selector);
  
  gsap.fromTo(
    items,
    { 
      opacity: 0,
      y: 30
    },
    { 
      opacity: 1,
      y: 0,
      duration: 0.5,
      stagger: 0.1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: items[0],
        start: 'top bottom-=100',
        toggleActions: 'play none none none'
      }
    }
  );
};

// SVG animation utilities
export const createSVGElements = () => {
  const svgShapes = [
    // SVG 1: Abstract geometric pattern
    `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" class="animated-svg svg-1">
      <path d="M30,10 L170,10 L170,190 L30,190 Z" fill="none" stroke-width="2" />
      <circle cx="100" cy="100" r="50" fill="none" stroke-width="2" />
      <path d="M30,100 L170,100" stroke-width="2" stroke-linecap="round" />
      <path d="M100,30 L100,170" stroke-width="2" stroke-linecap="round" />
    </svg>`,
    
    // SVG 2: Wave pattern
    `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" class="animated-svg svg-2">
      <path d="M10,50 C30,30 40,70 60,50 C80,30 90,70 110,50 C130,30 140,70 160,50 C180,30 190,70 210,50" 
        fill="none" stroke-width="2" stroke-linecap="round" />
      <path d="M10,100 C30,80 40,120 60,100 C80,80 90,120 110,100 C130,80 140,120 160,100 C180,80 190,120 210,100" 
        fill="none" stroke-width="2" stroke-linecap="round" />
      <path d="M10,150 C30,130 40,170 60,150 C80,130 90,170 110,150 C130,130 140,170 160,150 C180,130 190,170 210,150" 
        fill="none" stroke-width="2" stroke-linecap="round" />
    </svg>`,
    
    // SVG 3: Abstract shapes
    `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" class="animated-svg svg-3">
      <path d="M20,20 L80,20 L50,80 Z" />
      <path d="M120,20 L180,20 L150,80 Z" />
      <path d="M70,120 L130,120 L100,180 Z" />
    </svg>`,
    
    // SVG 4: Circular pattern
    `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" class="animated-svg svg-4">
      <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
      <circle cx="100" cy="100" r="60" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
      <circle cx="100" cy="100" r="40" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
      <line x1="20" y1="100" x2="180" y2="100" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
      <line x1="100" y1="20" x2="100" y2="180" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
    </svg>`,
    
    // SVG 5: Decorative arrows
    `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" class="animated-svg svg-5">
      <path d="M40,40 L160,40 L100,100 Z" fill="rgba(255,255,255,0.1)" />
      <path d="M40,160 L160,160 L100,100 Z" fill="rgba(255,255,255,0.1)" />
      <path d="M40,40 L40,160 L100,100 Z" fill="rgba(255,255,255,0.1)" />
      <path d="M160,40 L160,160 L100,100 Z" fill="rgba(255,255,255,0.1)" />
    </svg>`
  ];
  
  return svgShapes;
};

// Create floating particles for hero section
export const createFloatingParticles = () => {
  const particles = [
    '<div class="portfolio-floating-particle p1"></div>',
    '<div class="portfolio-floating-particle p2"></div>',
    '<div class="portfolio-floating-particle p3"></div>',
    '<div class="portfolio-floating-particle p4"></div>'
  ];
  
  return particles;
};

// Animation for CTA section
export const initCTAAnimation = () => {
  gsap.fromTo(
    '.cta-icon',
    { 
      scale: 0.8, 
      opacity: 0 
    },
    { 
      scale: 1, 
      opacity: 1, 
      duration: 1, 
      ease: 'elastic.out(1, 0.5)',
      scrollTrigger: {
        trigger: '.portfolio-cta',
        start: 'top bottom-=100',
        toggleActions: 'play none none none'
      }
    }
  );
  
  gsap.fromTo(
    '.cta-title, .cta-text',
    { 
      y: 30, 
      opacity: 0 
    },
    { 
      y: 0, 
      opacity: 1, 
      duration: 0.8, 
      stagger: 0.2,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.portfolio-cta',
        start: 'top bottom-=100',
        toggleActions: 'play none none none'
      }
    }
  );
  
  gsap.fromTo(
    '.cta-button',
    { 
      y: 20, 
      opacity: 0 
    },
    { 
      y: 0, 
      opacity: 1, 
      duration: 0.5, 
      delay: 0.6,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: '.portfolio-cta',
        start: 'top bottom-=100',
        toggleActions: 'play none none none'
      }
    }
  );
};

const PortfolioAnimations = {
  initHeroAnimations,
  animateFilterChange,
  animatePortfolioItems,
  staggerPortfolioItems,
  createSVGElements,
  createFloatingParticles,
  initCTAAnimation
};

export default PortfolioAnimations; 